import React, { useEffect } from "react";
import { HashtagNode } from "@lexical/hashtag";

import {
  InitialConfigType,
  LexicalComposer,
} from "@lexical/react/LexicalComposer";

import { PlainTextPlugin } from "@lexical/react/LexicalPlainTextPlugin";
import { RichTextPlugin } from "@lexical/react/LexicalRichTextPlugin";
import { ListPlugin } from "@lexical/react/LexicalListPlugin";
import { ContentEditable } from "@lexical/react/LexicalContentEditable";
import {
  HistoryPlugin,
  HistoryState,
} from "@lexical/react/LexicalHistoryPlugin";
import { LexicalErrorBoundary } from "@lexical/react/LexicalErrorBoundary";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { OnChangePlugin } from "@lexical/react/LexicalOnChangePlugin";
import { MarkdownShortcutPlugin } from "@lexical/react/LexicalMarkdownShortcutPlugin";
import { IDEA_PLANNER_MARKDOWN_TRANSFORMERS } from "@idea-planner/views/components/share-text-field/markdown-transformers";
import { HeadingNode, QuoteNode } from "@lexical/rich-text";
import { ListNode, ListItemNode } from "@lexical/list";
import { CodeNode } from "@lexical/code";
import { richTextTheme } from "./rich-text-theme";
import { commonTextExport, commonTextImport } from "./editor-state-conversion";
import { useEventCommands } from "./use-event-commands";
import { AtMentionNode } from "./at-mention/at-mention-node";
import {
  $getRoot,
  EditorState,
  ElementNode,
  HISTORIC_TAG,
  Klass,
  LexicalEditor,
  LexicalNode,
  LexicalNodeReplacement,
} from "lexical";
import { QueryClientProvider } from "@tanstack/react-query";
import { queryClient } from "../../api/query-client";
import { cn } from "../../lib/utils";

/**
 * SkedEditor - Lexical-based text editor with optional rich text support
 *
 * **Rich Text & Markdown Support (SKE-2727):**
 * When `enableRichText={true}`, the editor supports:
 * - PlainTextPlugin → RichTextPlugin
 * - Markdown shortcuts via MarkdownShortcutPlugin
 * - Rich text nodes: HeadingNode, QuoteNode, ListNode, ListItemNode, CodeNode
 * - Auto-conversion of markdown syntax (**, *, -, 1.) to formatted text
 * - Custom theme with Tailwind classes for styling (see rich-text-theme.ts)
 *
 * **Styling:**
 * The editor applies a custom Lexical theme when rich text is enabled.
 * Override by passing a custom `theme` prop. Defaults to empty theme for plain text.
 *
 * **Backward Compatibility:**
 * By default, `enableRichText={false}` maintains existing plain text behavior.
 * Only AI Planner fields currently enable rich text.
 */

export interface EditorProps {
  onCaptionChange?: (caption: string, state: EditorState) => void;
  plugins?: React.ReactNode;
  initInput?: InputContext;
  input?: Omit<TextInputContext, "type">;
  readonly?: boolean;
  disabled?: boolean;
  theme?: { [key: string]: string | undefined | object };
  className?: string;
  onEditorContextReady?: (params: {
    editor: LexicalEditor;
    syncExternalText?: (text: string) => void;
  }) => void;
  historyState?: HistoryState;
  initNodes?: (Klass<LexicalNode> | LexicalNodeReplacement)[];
  onEnterSubmit?: () => void;
  placeholder?: string;
  placeholderClassName?: string;
  htmlRef?: React.RefObject<HTMLDivElement>;
  contentHtmlRef?: React.RefObject<HTMLDivElement>;
  style?: React.CSSProperties;
  initEditorState?: EditorState | null;
  disableHistory?: boolean;
  contentEditableClassName?: string;
  /** Enable rich text editing with markdown support. Default: false for backward compatibility */
  enableRichText?: boolean;
}

const theme = {};

function onError(error: Error) {
  console.error(error);
}

export const SkedEditor = (props: EditorProps) => {
  // Include rich text nodes when enableRichText is true
  const richTextNodes = props.enableRichText
    ? [HeadingNode, QuoteNode, ListNode, ListItemNode, CodeNode]
    : [];

  const initialConfig: InitialConfigType = {
    namespace: "MyEditor",
    theme: props.theme || (props.enableRichText ? richTextTheme : theme),
    onError,
    nodes: [
      HashtagNode,
      AtMentionNode,
      ...richTextNodes,
      ...(props.initNodes || []),
    ],
    editorState: props.initEditorState,
  };
  return (
    <LexicalComposer initialConfig={initialConfig}>
      <Editor {...props} />
    </LexicalComposer>
  );
};

interface TextInputContext {
  type: "text";
  text: string;
  textImportProcess?: (text: string) => ElementNode | undefined;
}

interface StateInputContext {
  type: "state";
  editorState: EditorState;
}

export type InputContext = TextInputContext | StateInputContext;

export const Editor = ({
  onCaptionChange,
  plugins,
  initInput,
  readonly,
  disabled,
  className,
  onEditorContextReady,
  historyState,
  input,
  onEnterSubmit,
  placeholder,
  placeholderClassName,
  htmlRef,
  contentHtmlRef,
  style,
  disableHistory,
  contentEditableClassName,
  enableRichText = false,
}: EditorProps) => {
  const [editor] = useLexicalComposerContext();
  const [initHistoryState, setInitHistoryState] =
    React.useState<HistoryState>();
  const [initialized, setInitialized] = React.useState(false);
  const textRef = React.useRef<string>();
  const syncExternalText = React.useCallback((text: string) => {
    textRef.current = text;
  }, []);
  useEffect(() => {
    editor.setEditable(!disabled && !readonly);
  }, [editor, readonly, disabled]);

  useEffect(() => {
    if (editor) {
      onEditorContextReady?.({ editor, syncExternalText });
      setInitialized(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editor]);

  useEffect(() => {
    const onUpdate = () => {
      if (historyState) {
        if (historyState.current) {
          historyState.current.editor = editor;
        }
        for (const state of historyState.redoStack) {
          state.editor = editor;
        }
        for (const state of historyState.undoStack) {
          state.editor = editor;
        }
        setInitHistoryState(historyState);
      }
    };
    const tag = historyState ? HISTORIC_TAG : undefined;
    if (initInput?.type === "state") {
      editor.update(
        () => {
          editor.setEditorState(initInput.editorState, { tag });
        },
        { onUpdate }
      );
    } else if (initInput?.type === "text") {
      editor.update(
        () => {
          const importFunc = initInput.textImportProcess || commonTextImport;
          replaceRoot(importFunc(initInput.text));
        },
        { onUpdate }
      );
    } else {
      onUpdate();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (input && textRef.current !== input.text) {
      textRef.current = input.text;
      editor.update(() => {
        const importFunc = input.textImportProcess || commonTextImport;
        replaceRoot(importFunc(input.text));
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [input?.text]);

  const { isFocused } = useEventCommands(editor, { onEnterSubmit });

  return (
    <QueryClientProvider client={queryClient}>
      <div
        className={cn(
          `flex relative [&_p]:text-inherit [&_p]:text-[length:inherit] 
          [&_p]:leading-[inherit] [&_p]:font-[weight:inherit] [&_p]:m-[inherit] 
          data-focused:border-blue-400 transition-colors duration-200
          ${disabled ? "cursor-not-allowed bg-gray-100" : ""}`,
          className
        )}
        data-focused={isFocused ? "true" : undefined}
        ref={htmlRef}
        style={style}
        data-disabled={disabled ? "true" : undefined}
      >
        {enableRichText ? (
          <RichTextPlugin
            contentEditable={
              <ContentEditable
                className={cn(
                  `sked-editor-content-editable outline-none flex-1 p-0 overflow-auto whitespace-break-spaces`,
                  contentEditableClassName
                )}
                ref={contentHtmlRef}
                disabled={disabled}
                aria-placeholder={placeholder || ""}
                placeholder={
                  placeholder ? (
                    <span
                      className={cn(
                        ` text-gray-400 absolute left-0 top-0 pointer-events-none`,
                        placeholderClassName
                      )}
                    >
                      {placeholder}
                    </span>
                  ) : (
                    <span />
                  )
                }
              />
            }
            ErrorBoundary={LexicalErrorBoundary}
          />
        ) : (
          <PlainTextPlugin
            contentEditable={
              <ContentEditable
                className={cn(
                  `sked-editor-content-editable outline-none flex-1 p-0 overflow-auto whitespace-break-spaces`,
                  contentEditableClassName
                )}
                ref={contentHtmlRef}
                disabled={disabled}
                aria-placeholder={placeholder || ""}
                placeholder={
                  placeholder ? (
                    <span
                      className={cn(
                        ` text-gray-400 absolute left-0 top-0 pointer-events-none`,
                        placeholderClassName
                      )}
                    >
                      {placeholder}
                    </span>
                  ) : (
                    <span />
                  )
                }
              />
            }
            ErrorBoundary={LexicalErrorBoundary}
          />
        )}
      </div>
      <>
        {!disableHistory && (
          <HistoryPlugin externalHistoryState={initHistoryState} />
        )}
        {enableRichText && (
          <>
            <MarkdownShortcutPlugin
              transformers={IDEA_PLANNER_MARKDOWN_TRANSFORMERS}
            />
            <ListPlugin />
          </>
        )}
        {plugins}
        <OnChangePlugin
          onChange={(state, editor) => {
            if (!readonly && !disabled && onCaptionChange && initialized) {
              editor.read(() => {
                const textExported = commonTextExport();
                if (textRef.current !== textExported) {
                  textRef.current = textExported;
                  onCaptionChange(textExported, state);
                }
              });
            }
          }}
        />
      </>
    </QueryClientProvider>
  );
};

function replaceRoot(node: ElementNode | undefined) {
  if (!node) {
    return;
  }

  const root = $getRoot();
  root.clear();
  root.append(node);
}
